import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import {
  CreditCard, Globe, BookOpen, Users,
  CheckCircle2, Trash2, AlertCircle,
  Banknote, Scale, Home, GitBranch, HelpCircle, Check,
  Loader2, User, Lock, X, ArrowRight,
} from 'lucide-react';
import { Query, DocType, Motivo, RequesterInfo, RequesterDocType } from '../types';
import { pushDataLayer, getTrackingCookies } from '../utils/gtm';

const PRICE = 99000;

const DOC_VALIDATION: Record<DocType, { pattern: RegExp; hint: string; errorMsg: string }> = {
  CC: { pattern: /^\d{6,10}$/, hint: 'Solo números · 6–10 dígitos · Ej: 10234567', errorMsg: 'Formato incorrecto: 6–10 dígitos numéricos' },
  TI: { pattern: /^\d{10,11}$/, hint: 'Solo números · 10–11 dígitos · Ej: 1023456789', errorMsg: 'Formato incorrecto: 10–11 dígitos numéricos' },
  CE: { pattern: /^[A-Za-z0-9]{4,12}$/, hint: 'Alfanumérico · 4–12 caracteres · Ej: 458299', errorMsg: 'Formato incorrecto: 4–12 caracteres alfanuméricos' },
  PA: { pattern: /^[A-Za-z0-9]{5,12}$/, hint: 'Alfanumérico · 5–12 caracteres · Ej: AB1234567', errorMsg: 'Formato incorrecto: 5–12 caracteres alfanuméricos' },
};

const DOC_TYPES = [
  { value: 'CC' as DocType, abbr: 'C.C.', label: 'Cédula de Ciudadanía', desc: 'Nacional', Icon: CreditCard },
  { value: 'CE' as DocType, abbr: 'C.E.', label: 'Cédula de Extranjería', desc: 'Extranjeros', Icon: Globe },
  { value: 'PA' as DocType, abbr: 'PAS', label: 'Pasaporte', desc: 'Internacional', Icon: BookOpen },
  { value: 'TI' as DocType, abbr: 'T.I.', label: 'Tarjeta de Identidad', desc: 'Menores', Icon: Users },
];

const MOTIVOS: { value: Motivo; label: string; Icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { value: 'deudas', label: 'Cobro de deudas y/o obligaciones', Icon: Banknote },
  { value: 'divorcio', label: 'Proceso de divorcio', Icon: Scale },
  { value: 'arrendamiento', label: 'Contrato de arrendamiento', Icon: Home },
  { value: 'sucesion', label: 'Proceso de sucesión', Icon: GitBranch },
  { value: 'otro', label: 'Otro motivo', Icon: HelpCircle },
];

const DOC_LABELS: Record<DocType, string> = {
  CC: 'Cédula de Ciudadanía', CE: 'Cédula de Extranjería',
  PA: 'Pasaporte', TI: 'Tarjeta de Identidad',
};

const MOTIVO_LABELS: Record<Motivo, string> = {
  deudas: 'Cobro de deudas y/o obligaciones',
  divorcio: 'Proceso de divorcio',
  arrendamiento: 'Contrato de arrendamiento',
  sucesion: 'Proceso de sucesión',
  otro: 'Otro motivo',
};

const REQUESTER_DOC_TYPES: { value: RequesterDocType; abbr: string; label: string }[] = [
  { value: 'CC', abbr: 'C.C.', label: 'Cédula de Ciudadanía' },
  { value: 'CE', abbr: 'C.E.', label: 'Cédula de Extranjería' },
  { value: 'TI', abbr: 'T.I.', label: 'Tarjeta de Identidad' },
  { value: 'PA', abbr: 'PAS', label: 'Pasaporte' },
];

const REQUESTER_DOC_LABELS: Record<RequesterDocType, string> = {
  CC: 'Cédula de Ciudadanía', NIT: 'NIT',
  TI: 'Tarjeta de Identidad', PA: 'Pasaporte', CE: 'Cédula de Extranjería',
};

const PREFERRED_COUNTRIES = ['co', 'us', 'es', 'ec', 'mx', 'pa', 'pe', 'bo', 'ar', 'cl', 'uy'];

interface Props {
  queries: Query[];
  onAddQuery: (q: Query) => void;
  onRemoveQuery: (id: string) => void;
  showForm: boolean;
  onSetShowForm: (v: boolean) => void;
  verifyTrigger: number;
  onVerifyBackground: (query: Query) => void;
  onRequesterInfoCaptured: (info: RequesterInfo) => void;
  onShowAddAnotherPopup: () => void;
  removedQueryError?: string | null;
  onClearRemovedQueryError?: () => void;
}

type VerifyState = 'idle' | 'duplicate';

export default function Step1({
  queries, onAddQuery, onRemoveQuery, showForm, onSetShowForm,
  verifyTrigger, onVerifyBackground, onRequesterInfoCaptured,
  onShowAddAnotherPopup, removedQueryError, onClearRemovedQueryError,
}: Props) {
  // Investigation form
  const [docType, setDocType] = useState<DocType | null>(null);
  const [docNumber, setDocNumber] = useState('');
  const [motivo, setMotivo] = useState<Motivo | null>(null);
  const [otroTexto, setOtroTexto] = useState('');
  const [verifyState, setVerifyState] = useState<VerifyState>('idle');

  // Requester form (first query only)
  const [reqFirstName, setReqFirstName] = useState('');
  const [reqLastName, setReqLastName] = useState('');
  const [reqDocType, setReqDocType] = useState<RequesterDocType>('CC');
  const [reqDocNumber, setReqDocNumber] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqPhone, setReqPhone] = useState('+57');

  const handleVerifyRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (verifyTrigger > 0) handleVerifyRef.current();
  }, [verifyTrigger]);

  const docNumTrimmed = docNumber.trim();
  const isDocNumberValid = !!docType && docNumTrimmed.length >= 3 && DOC_VALIDATION[docType].pattern.test(docNumTrimmed);
  const showDocError = !!docType && docNumTrimmed.length >= 3 && !isDocNumberValid;

  const requirements = [
    { key: 'docType', label: 'Tipo de documento', done: !!docType },
    { key: 'docNumber', label: 'Número válido', done: isDocNumberValid },
    { key: 'motivo', label: 'Motivo de consulta', done: !!motivo },
  ];
  const completedCount = requirements.filter(r => r.done).length;
  const progressPct = (completedCount / 3) * 100;
  const isQueryFormValid = completedCount === 3;

  // Requester validation
  const isFirst = queries.length === 0;
  const isReqEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reqEmail.trim());
  const isReqPhoneValid = reqPhone.replace(/\D/g, '').length >= 10;
  const isReqFormValid = (
    reqFirstName.trim().length >= 2 &&
    reqLastName.trim().length >= 2 &&
    reqDocNumber.trim().length >= 4 &&
    isReqEmailValid &&
    isReqPhoneValid
  );

  const isValid = isFirst ? (isQueryFormValid && isReqFormValid) : isQueryFormValid;

  const resetForm = () => {
    setDocNumber(''); setMotivo(null); setDocType(null);
    setVerifyState('idle'); setOtroTexto('');
    setReqFirstName(''); setReqLastName(''); setReqDocType('CC');
    setReqDocNumber(''); setReqEmail(''); setReqPhone('+57');
  };

  const handleDocTypeChange = (dt: DocType) => {
    setDocType(dt);
    setVerifyState('idle');
    setDocNumber('');
  };

  const handleVerify = () => {
    if (!docType || !motivo || !isValid) return;
    if (queries.some(q => q.docType === docType && q.docNumber === docNumTrimmed)) {
      setVerifyState('duplicate');
      return;
    }

    pushDataLayer('form_submit_step1', {
      doc_type: docType, doc_number: docNumTrimmed,
      tracking: getTrackingCookies(),
    });

    const query: Query = {
      id: Date.now().toString(),
      docType, docTypeLabel: DOC_LABELS[docType],
      docNumber: docNumTrimmed, motivo,
      motivoLabel: motivo === 'otro' ? (otroTexto || 'Otro motivo') : MOTIVO_LABELS[motivo],
      otroTexto: motivo === 'otro' ? otroTexto : undefined,
      verifyStatus: 'pending',
      amount: PRICE,
    };

    onAddQuery(query);

    if (isFirst) {
      onRequesterInfoCaptured({
        firstName: reqFirstName.trim(),
        lastName: reqLastName.trim(),
        docType: reqDocType,
        docTypeLabel: REQUESTER_DOC_LABELS[reqDocType],
        docNumber: reqDocNumber.trim(),
        email: reqEmail.trim(),
        phone: reqPhone,
      });
    }

    onVerifyBackground(query);
    onShowAddAnotherPopup();
    resetForm();
    onSetShowForm(false);
  };

  handleVerifyRef.current = handleVerify;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Removed query error banner */}
      <AnimatePresence>
        {removedQueryError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 rounded-xl border-2 flex items-start gap-3"
            style={{ borderColor: '#FBBF24', backgroundColor: 'rgba(251,191,36,0.08)' }}
          >
            <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm flex-1" style={{ color: 'var(--text-primary)' }}>{removedQueryError}</p>
            <button
              onClick={() => onClearRemovedQueryError?.()}
              className="p-1 rounded-lg transition-all flex-shrink-0"
              style={{ color: 'var(--text-muted)' }}
            >
              <X size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Query cards */}
      <AnimatePresence>
        {queries.map((query, i) => (
          <PersonCard key={query.id} query={query} onRemove={() => onRemoveQuery(query.id)} index={i} />
        ))}
      </AnimatePresence>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="rounded-2xl p-8 border"
            style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-md)' }}
          >
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                {isFirst ? '¿A quién deseas consultar?' : 'Añadir otra consulta'}
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {isFirst
                  ? 'Ingresa los datos de la persona a investigar y tus datos de contacto.'
                  : 'Ingresa los datos de la siguiente persona a consultar.'}
              </p>
            </div>

            {/* Progress bar */}
            <div className="mb-7">
              <div className="flex items-center justify-between mb-2.5">
                {requirements.map((req, idx) => (
                  <div key={req.key} className="flex items-center gap-1.5">
                    <motion.div
                      animate={{ backgroundColor: req.done ? '#10B981' : 'var(--border-color)' }}
                      transition={{ duration: 0.3 }}
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      {req.done
                        ? <Check size={10} color="white" strokeWidth={3} />
                        : <span className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>{idx + 1}</span>}
                    </motion.div>
                    <span className="text-xs font-medium hidden sm:inline"
                      style={{ color: req.done ? '#10B981' : 'var(--text-muted)' }}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                <motion.div
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: progressPct === 100 ? '#10B981' : '#2563EB' }}
                />
              </div>
            </div>

            {/* Doc type cards */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Tipo de documento
              </label>
              <div className="grid grid-cols-2 gap-3">
                {DOC_TYPES.map(({ value, abbr, label, desc, Icon }) => (
                  <motion.button
                    key={value}
                    data-testid={`doc-type-${value}`}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => handleDocTypeChange(value)}
                    className="p-4 rounded-2xl border-2 text-left transition-all relative h-[100px] flex flex-col justify-between"
                    style={{
                      backgroundColor: docType === value ? 'rgba(37,99,235,0.06)' : 'var(--bg-surface)',
                      borderColor: docType === value ? '#2563EB' : 'var(--border-color)',
                    }}
                  >
                    {docType === value && <CheckCircle2 size={14} className="absolute top-2.5 right-2.5 text-[#2563EB]" />}
                    <div className="flex items-center gap-2">
                      <Icon size={18} className={docType === value ? 'text-[#2563EB]' : 'text-[var(--text-muted)]'} />
                      <p className={`text-base font-black leading-none ${docType === value ? 'text-[#2563EB]' : 'text-[var(--text-primary)]'}`}>{abbr}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--text-secondary)' }}>{label}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{desc}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
              <AnimatePresence>
                {(docType === 'PA' || docType === 'TI') && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                    className="text-sm mt-3" style={{ color: '#B45309' }}>
                    Importante: Con este tipo de documento puede que no sea posible establecer la identidad de forma definitiva. Recomendamos usar C.C. o C.E. cuando sea posible.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Doc number */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Número de documento
              </label>
              <div className="relative">
                <input
                  data-testid="doc-number-input"
                  type="text"
                  value={docNumber}
                  onChange={(e) => { setDocNumber(e.target.value); setVerifyState('idle'); }}
                  placeholder={docType ? `Ej: ${DOC_VALIDATION[docType].hint.split('· Ej: ')[1]}` : 'Selecciona tipo de documento primero'}
                  disabled={!docType}
                  className={`w-full px-4 py-3 pr-10 rounded-xl border-2 transition-all disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${
                    showDocError ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
                    : isDocNumberValid ? 'border-emerald-400 focus:border-emerald-400 focus:ring-emerald-100'
                    : 'focus:ring-[rgba(37,99,235,0.15)]'
                  }`}
                  style={{
                    fontFamily: 'JetBrains Mono, monospace', fontSize: '16px',
                    backgroundColor: 'var(--bg-surface)',
                    color: docType ? 'var(--text-primary)' : 'var(--text-muted)',
                    borderColor: showDocError ? undefined : isDocNumberValid ? undefined : 'var(--border-color)',
                  }}
                />
                <AnimatePresence>
                  {docType && docNumTrimmed.length >= 3 && (
                    <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }}
                      className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isDocNumberValid ? <CheckCircle2 size={18} className="text-emerald-500" /> : <AlertCircle size={18} className="text-red-400" />}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence mode="wait">
                {showDocError ? (
                  <motion.p key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs mt-1.5 font-medium" style={{ color: '#B91C1C' }}>
                    {DOC_VALIDATION[docType!].errorMsg}
                  </motion.p>
                ) : isDocNumberValid ? (
                  <motion.p key="ok" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs mt-1.5 font-medium" style={{ color: '#10B981' }}>
                    Formato correcto
                  </motion.p>
                ) : docType ? (
                  <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                    {DOC_VALIDATION[docType].hint}
                  </motion.p>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Motivo */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Motivo de consulta
              </label>
              <div className="grid grid-cols-1 gap-2">
                {MOTIVOS.map(({ value, label, Icon }) => (
                  <motion.button
                    key={value}
                    data-testid={`motivo-${value}`}
                    whileHover={{ scale: 1.005 }} whileTap={{ scale: 0.995 }}
                    onClick={() => setMotivo(value)}
                    className="p-3.5 rounded-xl border-2 text-left transition-all flex items-center gap-3"
                    style={{
                      backgroundColor: motivo === value ? 'rgba(37,99,235,0.06)' : 'var(--bg-surface)',
                      borderColor: motivo === value ? '#2563EB' : 'var(--border-color)',
                    }}
                  >
                    <Icon size={18} className={`flex-shrink-0 ${motivo === value ? 'text-[#2563EB]' : 'text-[var(--text-muted)]'}`} />
                    <span className={`text-sm font-medium flex-1 ${motivo === value ? 'text-[#2563EB]' : 'text-[var(--text-primary)]'}`}>{label}</span>
                    {motivo === value && <CheckCircle2 size={16} className="text-[#2563EB] flex-shrink-0" />}
                  </motion.button>
                ))}
              </div>
              <AnimatePresence>
                {motivo === 'otro' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3 overflow-hidden">
                    <textarea
                      data-testid="otro-motivo-input"
                      value={otroTexto}
                      onChange={(e) => setOtroTexto(e.target.value.slice(0, 120))}
                      placeholder="Describe brevemente el motivo de tu consulta..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-[rgba(37,99,235,0.15)] resize-none text-sm transition-all"
                      style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                    <p className="text-xs text-right mt-0.5" style={{ color: 'var(--text-muted)' }}>{otroTexto.length}/120 caracteres</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Duplicate error */}
            <AnimatePresence>
              {verifyState === 'duplicate' && (
                <motion.p data-testid="duplicate-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-sm mb-4 font-medium" style={{ color: '#B45309' }}>
                  Este documento ya fue agregado a la consulta. Ingresa un número de documento diferente.
                </motion.p>
              )}
            </AnimatePresence>

            {/* ───── REQUESTER SECTION (first query only) ───── */}
            <AnimatePresence>
              {isFirst && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="border-t pt-6 mt-2 mb-5" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                        <User size={17} className="text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                          ¿Quién realiza la consulta?
                        </h3>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Tus datos son confidenciales. Los usamos solo para enviarte el informe.
                        </p>
                      </div>
                    </div>

                    {/* Name row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>Nombres *</label>
                        <input
                          data-testid="req-first-name-input"
                          value={reqFirstName}
                          onChange={e => setReqFirstName(e.target.value)}
                          placeholder="Tu nombre"
                          className="dm-input w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:border-[#2563EB] focus:ring-[rgba(37,99,235,0.15)] transition-all"
                          style={{
                            fontSize: '16px',
                            backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                            borderColor: reqFirstName.trim().length >= 2 ? '#10B981' : 'var(--border-color)',
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>Apellidos *</label>
                        <input
                          data-testid="req-last-name-input"
                          value={reqLastName}
                          onChange={e => setReqLastName(e.target.value)}
                          placeholder="Tus apellidos"
                          className="dm-input w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:border-[#2563EB] focus:ring-[rgba(37,99,235,0.15)] transition-all"
                          style={{
                            fontSize: '16px',
                            backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                            borderColor: reqLastName.trim().length >= 2 ? '#10B981' : 'var(--border-color)',
                          }}
                        />
                      </div>
                    </div>

                    {/* Email + Phone row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>Correo electrónico *</label>
                        <input
                          data-testid="req-email-input"
                          type="email"
                          value={reqEmail}
                          onChange={e => setReqEmail(e.target.value)}
                          placeholder="tu@correo.com"
                          className="dm-input w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:border-[#2563EB] focus:ring-[rgba(37,99,235,0.15)] transition-all"
                          style={{
                            fontSize: '16px',
                            backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                            borderColor: reqEmail.trim().length > 0 ? (isReqEmailValid ? '#10B981' : '#F87171') : 'var(--border-color)',
                          }}
                        />
                        {reqEmail.trim().length > 0 && !isReqEmailValid && (
                          <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>Correo electrónico inválido</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>Número de celular *</label>
                        <div className="phone-wrapper">
                          <PhoneInput
                            data-testid="req-phone-input"
                            defaultCountry="co"
                            value={reqPhone}
                            onChange={(val) => setReqPhone(val)}
                            preferredCountries={PREFERRED_COUNTRIES}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Requester doc type */}
                    <div className="mb-1">
                      <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Tu tipo de documento *</label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {REQUESTER_DOC_TYPES.map(({ value, abbr, label }) => (
                          <motion.button
                            key={value}
                            type="button"
                            data-testid={`req-doc-type-${value}`}
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={() => setReqDocType(value)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all"
                            style={{
                              backgroundColor: reqDocType === value ? 'rgba(37,99,235,0.08)' : 'var(--bg-surface)',
                              borderColor: reqDocType === value ? '#2563EB' : 'var(--border-color)',
                              color: reqDocType === value ? '#2563EB' : 'var(--text-secondary)',
                            }}
                          >
                            {reqDocType === value && <CheckCircle2 size={12} />}
                            <span>{abbr}</span>
                          </motion.button>
                        ))}
                      </div>
                      <input
                        data-testid="req-doc-number-input"
                        value={reqDocNumber}
                        onChange={e => setReqDocNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="Tu número de documento"
                        className="dm-input w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:border-[#2563EB] focus:ring-[rgba(37,99,235,0.15)] transition-all"
                        style={{
                          fontFamily: 'JetBrains Mono, monospace', fontSize: '16px',
                          backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                          borderColor: reqDocNumber.trim().length >= 4 ? '#10B981' : 'var(--border-color)',
                        }}
                      />
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <p className="text-xs mb-5 flex items-start gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                    <Lock size={12} className="flex-shrink-0 mt-0.5" />
                    Al continuar acepto los <span className="underline cursor-pointer">términos y condiciones</span> y la <span className="underline cursor-pointer">política de privacidad</span>. Tus datos están protegidos bajo la Ley 1581 de 2012.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* CTA: first query button OR instruction for 2nd+ */}
            {queries.length === 0 ? (
              <motion.button
                data-testid="verify-btn"
                whileHover={isValid ? { scale: 1.01 } : {}}
                whileTap={isValid ? { scale: 0.99 } : {}}
                onClick={handleVerify}
                disabled={!isValid}
                className="w-full py-4 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#2563EB', boxShadow: '0 4px 20px rgba(37,99,235,0.25)' }}
              >
                Identificar y continuar
                <ArrowRight size={18} />
              </motion.button>
            ) : (
              <p className="text-sm text-center py-2" style={{ color: 'var(--text-secondary)' }}>
                Completa los datos y haz clic en <strong style={{ color: 'var(--text-primary)' }}>Verificar y agregar</strong> en la barra inferior.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PersonCard({ query, onRemove, index }: { query: Query; onRemove: () => void; index: number }) {
  const fmtCOP = (n: number) => new Intl.NumberFormat('es-CO').format(n);
  const isPending = query.verifyStatus === 'pending';
  const isVerified = query.verifyStatus === 'verified';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }} transition={{ delay: index * 0.04 }}
      className={`mb-4 p-5 rounded-2xl border-2 ${
        isPending
          ? 'border-blue-200 dark:border-blue-800'
          : isVerified
          ? 'border-emerald-200 dark:border-emerald-800'
          : 'border-amber-200 dark:border-amber-800'
      }`}
      style={{
        backgroundColor: isPending
          ? 'rgba(37,99,235,0.04)'
          : isVerified
          ? 'rgba(16,185,129,0.04)'
          : 'rgba(245,158,11,0.04)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            {isPending
              ? <Loader2 size={16} className="text-[#2563EB] animate-spin flex-shrink-0" />
              : isVerified
              ? <CheckCircle2 size={16} className="text-[#10B981] flex-shrink-0" />
              : <AlertCircle size={16} className="text-[#B45309] flex-shrink-0" />}
            <span className={`text-sm font-bold ${isPending ? 'text-[#2563EB]' : isVerified ? 'text-[#10B981]' : 'text-[#B45309]'}`}>
              {isPending ? 'Verificando identidad...' : isVerified ? 'Persona verificada' : 'No verificada'}
            </span>
          </div>
          <p className="font-bold text-lg leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
            {query.nombreCompleto || `${query.docTypeLabel} · ${query.docNumber}`}
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            {query.docTypeLabel} · <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{query.docNumber}</span>
          </p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Motivo: {query.motivoLabel}</p>
          {!isPending && !isVerified && (
            <p className="text-xs mt-1" style={{ color: '#B45309' }}>No fue posible confirmar el nombre con este documento.</p>
          )}
          <p className="text-sm font-bold mt-2" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-primary)' }}>
            Valor: ${fmtCOP(query.amount)} COP
          </p>
        </div>
        <motion.button
          data-testid={`remove-query-${query.id}`}
          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onClick={onRemove}
          className="p-2 rounded-xl hover:text-[#B91C1C] hover:bg-red-50 transition-all flex-shrink-0"
          style={{ color: 'var(--text-muted)' }}
        >
          <Trash2 size={16} />
        </motion.button>
      </div>
    </motion.div>
  );
}
